import React from 'react';

// Skeleton loading component with shimmer animation
export function Skeleton({ className = '', variant = 'default' }) {
  const baseClass = "animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer";
  
  const variants = {
    default: "rounded-lg",
    circle: "rounded-full",
    text: "rounded h-4",
    title: "rounded h-8",
    card: "rounded-2xl",
    avatar: "rounded-full w-12 h-12",
    button: "rounded-xl h-12",
    image: "rounded-xl aspect-video",
  };

  return (
    <div className={`${baseClass} ${variants[variant] || variants.default} ${className}`} />
  );
}

// Dashboard Stats Skeleton
export function StatsSkeleton() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <Skeleton className="w-12 h-12 rounded-xl" />
            <Skeleton className="w-16 h-6" variant="text" />
          </div>
          <Skeleton className="w-24 h-10 mb-2" variant="title" />
          <Skeleton className="w-20 h-4" variant="text" />
        </div>
      ))}
    </div>
  );
}

// Chart Skeleton
export function ChartSkeleton() {
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100">
      <div className="flex items-center gap-3 mb-6">
        <Skeleton className="w-8 h-8 rounded-lg" />
        <Skeleton className="w-40 h-6" variant="text" />
      </div>
      <div className="h-64 flex items-end justify-around gap-2 px-4">
        {[...Array(7)].map((_, i) => (
          <Skeleton 
            key={i} 
            className="flex-1 rounded-t-lg" 
            style={{ height: `${Math.random() * 60 + 40}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// Vehicle Card Skeleton
export function VehicleCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
      <Skeleton className="h-44 rounded-none" variant="image" />
      <div className="p-5">
        <Skeleton className="w-3/4 h-6 mb-2" variant="text" />
        <Skeleton className="w-1/2 h-4 mb-4" variant="text" />
        <div className="flex items-center justify-between">
          <Skeleton className="w-24 h-8" variant="title" />
          <div className="flex gap-2">
            <Skeleton className="w-10 h-10 rounded-lg" />
            <Skeleton className="w-10 h-10 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Lead Card Skeleton
export function LeadCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
      <Skeleton className="h-32 rounded-none" />
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <Skeleton className="w-3/4 h-5 mb-2" variant="text" />
            <Skeleton className="w-1/2 h-4" variant="text" />
          </div>
          <Skeleton className="w-20 h-6 rounded-full" />
        </div>
        <div className="space-y-2 my-4">
          <Skeleton className="w-full h-4" variant="text" />
          <Skeleton className="w-2/3 h-4" variant="text" />
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <Skeleton className="w-16 h-4" variant="text" />
          <Skeleton className="w-28 h-10 rounded-xl" variant="button" />
        </div>
      </div>
    </div>
  );
}

// Table Skeleton
export function TableSkeleton({ rows = 5 }) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
      <div className="bg-gray-50 px-6 py-4 flex gap-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="flex-1 h-4" variant="text" />
        ))}
      </div>
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="px-6 py-4 flex gap-4 border-t border-gray-100">
          {[...Array(5)].map((_, j) => (
            <Skeleton key={j} className="flex-1 h-4" variant="text" />
          ))}
        </div>
      ))}
    </div>
  );
}

// Form Skeleton
export function FormSkeleton() {
  return (
    <div className="space-y-6">
      {[...Array(4)].map((_, i) => (
        <div key={i}>
          <Skeleton className="w-24 h-4 mb-2" variant="text" />
          <Skeleton className="w-full h-12 rounded-xl" />
        </div>
      ))}
      <Skeleton className="w-full h-12 rounded-xl" variant="button" />
    </div>
  );
}

// Page Loading Skeleton
export function PageLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar Skeleton */}
      <div className="hidden lg:block w-72 bg-white border-r border-gray-200 p-6">
        <Skeleton className="h-12 w-32 mb-8" />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="w-full h-12 rounded-xl" />
          ))}
        </div>
        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
            <Skeleton variant="avatar" />
            <div className="flex-1">
              <Skeleton className="w-24 h-4 mb-1" variant="text" />
              <Skeleton className="w-32 h-3" variant="text" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content Skeleton */}
      <div className="flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Skeleton className="w-48 h-8 mb-2" variant="title" />
            <Skeleton className="w-32 h-4" variant="text" />
          </div>
          <Skeleton className="w-36 h-12 rounded-xl" variant="button" />
        </div>
        <StatsSkeleton />
        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      </div>
    </div>
  );
}

// Full Page Loader
export function FullPageLoader() {
  return (
    <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-100 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin absolute inset-0"></div>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
          <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
        </div>
        <p className="text-gray-500 font-medium">Loading...</p>
      </div>
    </div>
  );
}

// Inline Loader
export function InlineLoader({ size = 'md', color = 'blue' }) {
  const sizes = {
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
    lg: 'w-8 h-8 border-3',
    xl: 'w-12 h-12 border-4',
  };
  
  const colors = {
    blue: 'border-blue-500',
    purple: 'border-purple-500',
    green: 'border-green-500',
    white: 'border-white',
  };

  return (
    <div className={`${sizes[size]} ${colors[color]} border-t-transparent rounded-full animate-spin`}></div>
  );
}

// Pulse Dot Loader
export function PulseDotLoader() {
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"
          style={{ animationDelay: `${i * 200}ms` }}
        />
      ))}
    </div>
  );
}

export default {
  Skeleton,
  StatsSkeleton,
  ChartSkeleton,
  VehicleCardSkeleton,
  LeadCardSkeleton,
  TableSkeleton,
  FormSkeleton,
  PageLoadingSkeleton,
  FullPageLoader,
  InlineLoader,
  PulseDotLoader,
};
